import Joi from "joi";
import tlds from "tlds";
import {
  isAllowHyphenAposEnabled,
  isAllowNumDotEnabled,
  // isJoiValidationEnabled,
} from "../../../constant/featureSwitches";


let joiValidationEnabled = false;
export function setJoiValidationEnabled(enabled: boolean): void {
  joiValidationEnabled = enabled;
}

/**
 * Joi safety-net schemas for traveler-form name + email fields.
 *
 * These schemas ENCODE THE SAME RULES the existing regex helpers in
 * `validateForm.ts` already enforce (`isValidNameInput`, `isValidMandatoryName`,
 * `isValidMiddleName`, `isValidEmail`). They are layered INSIDE those helpers
 * as a second, schema-based pass — a value must satisfy BOTH the existing
 * checks AND the Joi schema to be considered valid.
 *
 * In steady state (rules agree) Joi never rejects independently; it only fires
 * if a future edit ever drifts one side. All existing behaviours (dynamic
 * First+Middle=28 length cap, blur-gated error copy, Next-click block +
 * auto-scroll, live email length/sanitize) are unchanged — this file only
 * provides pass/fail booleans; the specific error messages continue to come
 * from the existing helpers in `validateForm.ts`.
 *
 * Scope: names + email only. Mobile stays with libphonenumber-js (country-
 * specific; Joi has no equivalent). Passport stays with alphaNumericRegex.
 *
 * The two constants (`FIRST_LAST_MAX`, `EMAIL_MAX`) are inlined here to avoid
 * an import cycle with `validateForm.ts`; they mirror `NAME_LIMITS.first`/`.last`
 * and `EMAIL_MAX_LENGTH` in that file.
 */

/** Mirrors NAME_LIMITS.first / NAME_LIMITS.last in validateForm.ts. Kept in sync manually. */
const FIRST_LAST_MAX = 28;
/** Mirrors NAME_LIMITS.middle in validateForm.ts. Own-cap only — the dynamic First+Middle=28 combined budget is owned by getNameLengthError. */
const MIDDLE_OWN_MAX = 26;
/** Mirrors EMAIL_MAX_LENGTH in validateForm.ts. Kept in sync manually. */
const EMAIL_MAX = 92;

/**
 * Builds the allowed name character class from the two feature switches.
 * Base: letters + whitespace. Adds `0-9.` when ALLOW_NUM_DOT is on, `'-` when
 * ALLOW_HYPHEN_APOS is on. `-` stays LAST so it's a literal hyphen inside the
 * class, not a range. Matches the class assembled by `isValidNameInput`.
 */
function buildNameCharClass(): string {
  const numDot = isAllowNumDotEnabled() ? "0-9." : "";
  const hyphenApos = isAllowHyphenAposEnabled() ? "'-" : "";
  return `A-Za-z\\s${numDot}${hyphenApos}`;
}

/**
 * First / Last name schema: min 2, starts-with-letter, in-set chars only, ≤ 28.
 * Rebuilt on demand so it picks up the current runtime state of the char-set
 * switches (matches `isValidNameInput`'s per-call behaviour).
 */
export function getFirstLastNameSchema(): Joi.StringSchema {
  return Joi.string()
    .trim()
    .min(2)
    .max(FIRST_LAST_MAX)
    .pattern(new RegExp(`^[A-Za-z][${buildNameCharClass()}]*$`));
}

/**
 * Middle-name schema: empty OR (≥1 alphabet + in-set chars), ≤ own-cap 26.
 * Empty is valid (middle is optional). The dynamic First+Middle=28 combined
 * budget is NOT enforced here — it's already owned by `getNameLengthError` at
 * both the display and gate layers.
 */
export function getMiddleNameSchema(): Joi.StringSchema {
  return Joi.string()
    .trim()
    .allow("")
    .max(MIDDLE_OWN_MAX)
    .pattern(new RegExp(`^[${buildNameCharClass()}]*$`))
    .custom((value, helpers) => {
      if (value === "") return value;
      return /[A-Za-z]/.test(value) ? value : helpers.error("any.invalid");
    });
}

/**
 * Email schema: RFC-lite via Joi, ≤ 92. Uses the full IANA TLD list from the
 * `tlds` package as an explicit allow-list — rejects emails ending in a
 * non-existent TLD (e.g. `a@b.foo`, `a@b.zzzzz`) that `validator/isEmail`
 * accepts. `minDomainSegments: 2` requires at least `x@y.z` (rejects
 * `x@localhost`). Combined with `isEmail` in `validateForm.ts`'s
 * `isValidEmail` (both must pass), this gives us structural RFC compliance
 * from `validator` + real-TLD guarantee from `joi + tlds`.
 */
export const emailJoiSchema: Joi.StringSchema = Joi.string()
  .trim()
  .max(EMAIL_MAX)
  .email({ tlds: { allow: tlds }, minDomainSegments: 2 });

/**
 * Boolean pass-check helper — no message, no throw. `convert: false` disables
 * Joi's coercion so the check is pure (mirrors how the existing regex helpers
 * behave).
 *
 * FEATURE-SWITCH GATE: when `ENABLE_JOI_VALIDATION` is `"off"` this always
 * returns `true`, so every `existing && isJoiValid(...)` chain in
 * `validateForm.ts` collapses to just the existing regex check and the joi
 * fallback branches in `getMandatoryNameErrorMessage` /
 * `getMiddleNameErrorMessage` never fire — the traveler form behaves exactly
 * as it did before joi was introduced. Flip the switch back to `"on"` in
 * `featureSwitches.ts` to re-enable the layered check.
 */
export function isJoiValid(schema: Joi.Schema, value: unknown): boolean {
  if (!joiValidationEnabled) return true;
  return !schema.validate(value, { convert: false }).error;
}
